<script setup lang="ts">
import CircularProgress from '@/uiKit/CircularProgress.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import PopOver from '@/uiKit/PopOver.vue'
import { onKeyStroke } from '@vueuse/core'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { GRID_SCROLL_CONTAINER_ID } from './ProjectTable.vue'
import { usePreviewChanges } from './usePreviewChanges'
import { useProjectStore } from './useProjectStore'
import { useTable } from './useTable'
/**
 * Renders a Popover that allows the user to save or cancel changes made to
 * the preview range of cells.
 */

const { resetPreview, savePreviewChanges, savedEntityCount } = usePreviewChanges()

const projectStore = useProjectStore()
const tableStore = useTable()
const pctComplete = computed<number>(() => {
  if (!tableStore.previewRange || !projectStore.activeView?.entities) {
    return 100
  }

  const totalEntities =
    tableStore.previewRange.end.rowIndex - tableStore.previewRange.start.rowIndex + 1

  const completedEntities = isSaving.value
    ? savedEntityCount.value
    : projectStore.activeView.entities.length - tableStore.previewRange.start.rowIndex

  return Math.round((completedEntities / totalEntities) * 100)
})

const isSaving = ref(false)
const onSaveChanges = async () => {
  isSaving.value = true
  try {
    await savePreviewChanges()
  } finally {
    isSaving.value = false
  }
}

/**
 * Sets the inert attribute on the table element when saving, so that
 * the the user can't clear the preview range. This is a fairly blunt
 * solution, but we haven't prioritised bulk import via pasting yet so
 * this is a sticking plaster until we can implement a better solution.
 */
const setTableInert = (isSaving: boolean) => {
  const tableEl = document.getElementById(GRID_SCROLL_CONTAINER_ID)
  if (!tableEl) {
    return
  }

  if (isSaving) {
    tableEl.setAttribute('inert', '')
  } else {
    tableEl.removeAttribute('inert')
  }
}

/**
 * Don't let the user interact with the table while we're saving, because it
 * would clear the preview range and cause issues.
 */
watch(isSaving, (newIsSaving) => {
  setTableInert(newIsSaving)
})

onBeforeUnmount(() => {
  setTableInert(false)
})

onKeyStroke('Escape', (e) => {
  e.stopPropagation()
  resetPreview()
})

onKeyStroke('Enter', (e) => {
  e.stopPropagation()
  onSaveChanges()
})

const dialogLabelId = 'preview-changes-dialog-label'
</script>

<template>
  <PopOver
    :open="true"
    :auto-placement-options="{ allowedPlacements: ['top-start'] }"
    :offset="{ mainAxis: 24, alignmentAxis: -2 }"
    role="dialog"
    :aria-labelledby="dialogLabelId"
  >
    <template #content>
      <div
        class="flex h-9 items-center rounded-xl bg-surface-tertiary"
        data-theme="dark"
        data-test="preview-changes-dialog"
      >
        <div
          :id="dialogLabelId"
          class="pr-3 pl-4 text-sm-12px-default text-text"
        >
          <div
            v-if="pctComplete < 100"
            class="flex items-center gap-2 tabular-nums"
          >
            <div>{{ isSaving ? 'Saving' : 'Pasting' }} {{ pctComplete }}% complete</div>
            <CircularProgress size="sm" />
          </div>
          <template v-else> Previewing paste </template>
        </div>

        <div
          v-if="pctComplete >= 100"
          class="flex h-full items-center gap-1 rounded-xl bg-surface-primary px-1"
        >
          <DarwinButton
            variant="transparent"
            size="sm"
            @click="onSaveChanges"
          >
            Save
          </DarwinButton>
          <DarwinButton
            variant="critical-transparent"
            size="sm"
            @click="resetPreview"
          >
            Cancel
          </DarwinButton>
        </div>
      </div>
    </template>
  </PopOver>
</template>
